import Fuse from "fuse.js";

export default function documentationSearch() {
  return {
    query: "",
    results: [],
    selectedIndex: 0,
    fuse: null,
    loading: false,

    // The index is not embedded in the page: it is fetched the first time the
    // search is opened (see x-effect on the modal), then kept for the page.
    async loadIndex() {
      if (this.fuse || this.loading) return;

      const url = this.$el.dataset.searchIndexUrl;
      if (!url) {
        console.error("Search index URL not found in data-search-index-url");
        return;
      }

      this.loading = true;
      try {
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const searchIndex = await response.json();

        // Configure Fuse.js for fuzzy search.
        // - ignoreLocation: a word is found wherever it sits in the text. With
        //   the default location (0) and a distance of 100, only the first
        //   forty characters of a field were searched: the section titles at
        //   the end of a description never matched.
        // - ignoreDiacritics: "periodes" finds "périodes", "eleve" finds "élève".
        // - keywords: the labels of the buttons, fields and steps of a guide,
        //   searched but not displayed.
        // - useExtendedSearch: every word of the query must be found, in any
        //   order ("changer langue" finds the guide that says "Changement de
        //   langue"), instead of one long approximate pattern.
        const options = {
          keys: [
            { name: "title", weight: 3 },
            { name: "moduleName", weight: 1.5 },
            { name: "description", weight: 1.2 },
            { name: "keywords", weight: 1 },
          ],
          threshold: 0.25,
          useExtendedSearch: true,
          ignoreLocation: true,
          ignoreDiacritics: true,
          minMatchCharLength: 2,
          includeScore: true,
          includeMatches: true,
        };

        this.fuse = new Fuse(searchIndex, options);
        // A query typed while the index was loading
        if (this.query) this.search();
      } catch (error) {
        console.error("Error loading the search index:", error);
      } finally {
        this.loading = false;
      }
    },

    search() {
      if (!this.query) {
        this.results = [];
        this.selectedIndex = 0;
        return;
      }

      // Index still loading: loadIndex() runs the search when it is ready.
      if (!this.fuse) return;

      // The extended search reads | ! ^ = $ ' " as operators: they are
      // dropped, and so are the one-letter words left by an apostrophe.
      const terms = this.query
        .replace(/[|!^=$'"’]/g, " ")
        .split(/\s+/)
        .filter((term) => term.length >= 2);
      if (terms.length === 0) {
        this.results = [];
        this.selectedIndex = 0;
        return;
      }

      // Perform search
      const fuseResults = this.fuse.search(terms.join(" "));

      // Transform results and limit to top 10
      this.results = fuseResults.slice(0, 10).map((result) => ({
        ...result.item,
        score: result.score,
        matches: result.matches,
      }));

      this.selectedIndex = 0;
    },

    navigateResults(direction) {
      if (this.results.length === 0) return;

      if (direction === "down") {
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.results.length - 1,
        );
      } else if (direction === "up") {
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      }

      // Scroll selected item into view
      this.$nextTick(() => {
        const selected = this.$el.querySelector(
          `[data-result-index="${this.selectedIndex}"]`,
        );
        if (selected) {
          selected.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      });
    },

    selectResult() {
      if (this.results.length > 0 && this.results[this.selectedIndex]) {
        const result = this.results[this.selectedIndex];
        if (result.path && result.path !== "#") {
          window.location.href = result.path;
        }
      }
    },

    highlightMatch(text, matches) {
      if (!matches || matches.length === 0) return text;

      // Simple highlighting - can be improved
      return text;
    },

    getTypeLabel(type) {
      // Get current locale from meta tag
      const locale =
        document.querySelector('meta[name="current-locale"]')?.content || "fr";

      const labels = {
        fr: {
          module: "Module",
          section: "Section",
          topic: "Sujet populaire",
          space: "Espace",
        },
        en: {
          module: "Module",
          section: "Section",
          topic: "Popular topic",
          space: "Space",
        },
      };

      return labels[locale]?.[type] || labels["fr"][type] || type;
    },

    getTypeColor(type) {
      const colors = {
        module: "bg-primary/10 text-primary",
        section: "bg-blue-500/10 text-blue-500",
        topic: "bg-cta/10 text-cta",
        space: "bg-cta/10 text-cta",
      };
      return colors[type] || "bg-gray-4 text-gray-2";
    },
  };
}
